import React, { useMemo, useState } from "react";

/** Video count per day/month bucket (not views/likes/comments). Default for creator overview chart. */
export const CREATOR_DAILY_BARS_DEFAULT_METRIC = "count";

const METRICS = [
  { value: CREATOR_DAILY_BARS_DEFAULT_METRIC, label: "Videos" },
  { value: "views", label: "Views" },
  { value: "likes", label: "Likes" },
  { value: "comments", label: "Comments" },
];

const formatDay = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const formatMonth = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(`${dateStr}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

/**
 * Daily or monthly buckets from API (oldest first): { date, views, likes, comments, count }.
 * ``daily`` and ``monthly`` are both optional; the toggle picks which series to render.
 */
const CreatorDailyBars = ({
  daily = [],
  monthly = [],
  heightClass = "h-16",
  defaultMetric = CREATOR_DAILY_BARS_DEFAULT_METRIC,
}) => {
  const [range, setRange] = useState("day");
  const [metric, setMetric] = useState(defaultMetric);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const isMonth = range === "month";
  const rawRows = isMonth ? monthly : daily;

  const { points, maxVal } = useMemo(() => {
    const rows = Array.isArray(rawRows) ? rawRows : [];
    const vals = rows.map((d) => Number(d[metric]) || 0);
    const m = Math.max(1, ...vals);
    return { points: rows.map((d, i) => ({ ...d, v: vals[i] })), maxVal: m };
  }, [rawRows, metric]);

  const rangeLabel = isMonth ? "Past 12 months" : "Past ~30 days";

  if (!points.length) {
    return (
      <div className={`flex items-center justify-center text-theme-text opacity-50 text-sm ${heightClass}`}>
        No videos in this window yet.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs uppercase tracking-wider text-theme-text opacity-70">{rangeLabel}</span>
          <div className="flex gap-0.5 rounded border border-theme-secondary bg-theme-main p-0.5">
            <button
              type="button"
              onClick={() => setRange("day")}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                !isMonth ? "bg-theme-active-nav text-theme-text" : "text-theme-text opacity-70 hover:opacity-100"
              }`}
            >
              Past Month
            </button>
            <button
              type="button"
              onClick={() => setRange("month")}
              className={`rounded px-2 py-0.5 text-xs font-medium transition-colors ${
                isMonth ? "bg-theme-active-nav text-theme-text" : "text-theme-text opacity-70 hover:opacity-100"
              }`}
            >
              Past Year
            </button>
          </div>
        </div>
        <select
          value={metric}
          onChange={(e) => setMetric(e.target.value)}
          className="rounded border border-theme-secondary bg-theme-main px-2 py-1 text-xs text-theme-text"
        >
          {METRICS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      <div className={`relative ${heightClass} w-full`}>
        {hoveredIndex != null && points[hoveredIndex] && (
          <div
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded border border-theme-secondary bg-theme-secondary px-2 py-1.5 text-xs text-theme-text shadow-lg"
            style={{
              left: `${((hoveredIndex + 0.5) / points.length) * 100}%`,
              transform: "translateX(-50%)",
              bottom: "100%",
              marginBottom: "4px",
            }}
          >
            <div className="font-medium">
              {isMonth ? formatMonth(points[hoveredIndex].date) : formatDay(points[hoveredIndex].date)}
            </div>
            <div className="mt-0.5 tabular-nums text-theme-text opacity-80">
              {METRICS.find((x) => x.value === metric)?.label}: {points[hoveredIndex].v}
            </div>
          </div>
        )}
        <div className="absolute inset-0 flex items-end gap-px">
          {points.map((p, i) => {
            const heightPct = maxVal > 0 ? (p.v / maxVal) * 100 : 0;
            const isHovered = hoveredIndex === i;
            return (
              <div
                key={`${p.date}-${i}`}
                className={`min-w-0 flex-1 cursor-pointer rounded-t-sm bg-theme-text transition-opacity ${
                  isHovered ? "opacity-60" : "opacity-15 hover:opacity-25"
                }`}
                style={{ height: `${Math.max(heightPct, p.v > 0 ? 6 : 2)}%` }}
                onMouseEnter={() => setHoveredIndex(i)}
                onMouseLeave={() => setHoveredIndex(null)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CreatorDailyBars;
